import { InfoIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertIcon,
  AlertTitle,
  Button,
  CardBody,
  CardFooter,
  CardHeader,
  Checkbox,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightAddon,
  Stack,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslations } from 'use-intl';

import { noop } from '@blockpulse3/data/shared';
import { CompanyDraftContent } from '@blockpulse3/graphql/hooks';
import { ErrorMessage } from '@blockpulse3/ui/commons';

import { companyDraftContentDefaultValues, schemaFinancial } from '../../provider/schema';

type Props = {
  defaultValues?: CompanyDraftContent;
  onCancel: () => void;
  onSubmit: (data: CompanyDraftContent) => void;
};

/**
 * FinancialInformationsForm.
 * Form for the financial informations of a SPV.
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function FinancialInformationsForm({
  defaultValues = companyDraftContentDefaultValues,
  onCancel = noop,
  onSubmit,
}: Props): JSX.Element {
  const i18nGeneral = useTranslations('General');
  const i18nFinancialInformationsForm = useTranslations('FinancialInformationsForm');

  const { register, formState, watch, handleSubmit } = useForm<CompanyDraftContent>({
    defaultValues,
    resolver: yupResolver(schemaFinancial),
  });

  const carriedInterest = watch('carriedInterest');
  const compensation = watch('compensation');

  const isCarriedChecked = carriedInterest > 0;
  const isPresidentChecked = compensation > 0;

  const handleFormCancel = (): void => {
    onCancel();
  };

  const handleFormSubmit: SubmitHandler<CompanyDraftContent> = (data) => {
    if (!data.nominalValue) data.nominalValue = 1;
    if (data.carriedInterest <= 0) {
      data.initialShareholder.preferredSharesPercentages = 0;
    } else if (data.initialShareholder?.capitalDeposit < 2) {
      data.initialShareholder.capitalDeposit = 2;
    }
    onSubmit(data);
  };

  return (
    <>
      <CardHeader as={Stack} spacing="2">
        <Heading size="lg">{i18nFinancialInformationsForm('FormTitle')}</Heading>
        <Text color="gray.600" fontSize="lg">
          {i18nFinancialInformationsForm('FormSubTitle')}
        </Text>
      </CardHeader>
      <Divider />
      <CardBody>
        <form id="financial-informations" onSubmit={handleSubmit(handleFormSubmit)}>
          <Stack spacing="4">
            <HStack alignItems="space-betwwen" bg="gray.50" p="3" rounded="md">
              <HStack alignItems="flex-start" flexGrow="1">
                <Checkbox bg="white" isChecked={isPresidentChecked} mt="1" />
                <FormControl isInvalid={!!formState.errors?.compensation}>
                  <FormLabel htmlFor="compensation">
                    {i18nFinancialInformationsForm('CompensationField')}
                  </FormLabel>
                  <InputGroup w={{ base: 'full', md: '49%' }}>
                    <Input
                      id="compensation"
                      step="0.01"
                      type="number"
                      {...register('compensation')}
                    />
                    <InputRightAddon>€</InputRightAddon>
                  </InputGroup>
                  <ErrorMessage error={formState.errors.compensation} />
                </FormControl>
              </HStack>
              <Tooltip
                hasArrow
                label={i18nFinancialInformationsForm('Tooltip.CompensationField')}
                placement="top"
              >
                <Icon as={InfoIcon} color="gray.500" />
              </Tooltip>
            </HStack>
            <HStack alignItems="space-betwwen" bg="gray.50" p="3" rounded="md">
              <HStack alignItems="flex-start" flexGrow="1">
                <Checkbox bg="white" isChecked={isCarriedChecked} mt="1" />
                <FormControl isInvalid={!!formState.errors?.carriedInterest}>
                  <FormLabel htmlFor="carriedInterest">
                    {i18nFinancialInformationsForm('CarriedInterestField')}
                  </FormLabel>
                  <InputGroup w={{ base: 'full', md: '49%' }}>
                    <Input
                      id="carriedInterest"
                      step="0.01"
                      type="number"
                      {...register('carriedInterest')}
                    />
                    <InputRightAddon>%</InputRightAddon>
                  </InputGroup>
                  <ErrorMessage error={formState.errors.carriedInterest} />
                </FormControl>
              </HStack>
              <Tooltip
                hasArrow
                label={i18nFinancialInformationsForm('Tooltip.CarriedInterestField')}
                placement="top"
              >
                <Icon as={InfoIcon} color="gray.500" />
              </Tooltip>
            </HStack>
            <Stack bg="gray.50" p="3" rounded="md">
              <HStack alignItems="space-between">
                <FormControl alignItems="flex-start" as={HStack}>
                  <Checkbox disabled mt="1" />
                  <FormLabel mb="0">{i18nFinancialInformationsForm('InvestCostField')}</FormLabel>
                </FormControl>
                <Tooltip
                  hasArrow
                  label={i18nFinancialInformationsForm('Tooltip.InvestCostField')}
                  placement="top"
                >
                  <Icon as={InfoIcon} color="gray.500" />
                </Tooltip>
              </HStack>
              <Alert status="info" w="full">
                <AlertIcon />
                <AlertTitle>{i18nFinancialInformationsForm('InvestCostInfo')}</AlertTitle>
              </Alert>
            </Stack>
          </Stack>
        </form>
      </CardBody>
      <CardFooter as={Stack} direction={{ base: 'column', md: 'row' }} spacing="4">
        <Button type="button" variant="secondary" w="full" onClick={handleFormCancel}>
          {i18nGeneral('Back')}
        </Button>
        <Button form="financial-informations" type="submit" w="full">
          {i18nGeneral('Next')}
        </Button>
      </CardFooter>
    </>
  );
}

export type FinancialInformationsFormProps = Props;
